import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Download, Lock } from 'lucide-react';

// These two columns are ALWAYS included first — required for re-import idempotency
const MANDATORY_COLS = [
  { key: 'deal_id', label: 'Deal ID' },
  { key: 'status',  label: 'Status' },
];

// Optional columns the user can toggle
const COLUMN_GROUPS = [
  {
    label: 'DEAL IDENTIFICATION',
    columns: [
      { key: 'deal_name',     label: 'Deal Name' },
      { key: 'merchant_name', label: 'Merchant Name' },
      { key: 'funding_date',  label: 'Funding Date' },
    ],
  },
  {
    label: 'DEAL ECONOMICS',
    columns: [
      { key: 'funding_amount', label: 'Advance Amount' },
      { key: 'rate',           label: 'Factor Rate' },
      { key: 'term_days',      label: 'Term (Days)' },
      { key: 'fees',           label: 'Fee Amount' },
    ],
  },
  {
    label: 'PERFORMANCE',
    columns: [
      { key: 'payments_made', label: 'Payments Made' },
      { key: 'balance',       label: 'Remaining Balance (RTR)' },
    ],
  },
];

const ALL_KEYS = COLUMN_GROUPS.flatMap(g => g.columns.map(c => c.key));

// Round a numeric value to 2 decimal places, treating near-zero floats as 0
function roundFinancial(val) {
  if (val == null || val === '') return '';
  const n = parseFloat(val);
  if (isNaN(n)) return val;
  // Treat values smaller than 0.005 in absolute terms as zero
  if (Math.abs(n) < 0.005) return 0;
  return Math.round(n * 100) / 100;
}

// Map a deal object + column key → CSV cell value
function getCellValue(deal, key) {
  switch (key) {
    case 'deal_id':
      return deal.deal_id != null ? deal.deal_id : '';
    case 'status':
      return deal.status || '';
    case 'deal_name':
      return deal.deal_name || deal.deal_id || '';
    case 'merchant_name':
      return deal.company_name || deal.merchant || deal.business_name || '';
    case 'funding_date': {
      const d = deal.funding_date;
      if (!d) return '';
      const date = new Date(d);
      if (isNaN(date.getTime())) return d;
      return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
    }
    case 'funding_amount':
      return roundFinancial(deal.funding_amount);
    case 'rate':
      return deal.rate != null ? parseFloat(deal.rate) || '' : '';
    case 'term_days':
      return deal.term_days != null ? deal.term_days : '';
    case 'fees':
      return roundFinancial(deal.fees);
    case 'payments_made':
      return roundFinancial(deal.payments_made);
    case 'balance':
      return roundFinancial(deal.balance);
    default:
      return '';
  }
}

// Escape a CSV cell
function csvCell(val) {
  const str = val == null ? '' : String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export default function DealsCSVExportModal({ open, onClose, deals }) {
  const [selected, setSelected] = useState(() => new Set(ALL_KEYS));

  const allSelected = selected.size === ALL_KEYS.length;
  const noneSelected = selected.size === 0;

  const toggleKey = useCallback((key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(allSelected ? new Set() : new Set(ALL_KEYS));
  }, [allSelected]);

  const handleDownload = useCallback(() => {
    const optionalCols = COLUMN_GROUPS.flatMap(g =>
      g.columns.filter(c => selected.has(c.key))
    );

    // Mandatory first, then optional
    const allCols = [...MANDATORY_COLS, ...optionalCols];

    const header = allCols.map(c => csvCell(c.label)).join(',');
    const rows = deals.map(deal =>
      allCols.map(c => csvCell(getCellValue(deal, c.key))).join(',')
    );

    const csv = [header, ...rows].join('\n');
    const today = new Date().toISOString().split('T')[0];
    const filename = `deals_export_${today}.csv`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    onClose();
  }, [deals, selected, onClose]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="csv-export-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-blue-600" />
            Download Deals CSV
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-500 -mt-1">
          {deals.length} deal{deals.length !== 1 ? 's' : ''} will be exported.
          Select the optional columns to include:
        </p>

        {/* Mandatory columns notice */}
        <div className="bg-gray-50 rounded-md px-3 py-2 border border-gray-200">
          <p className="text-xs font-semibold tracking-wide text-gray-400 uppercase mb-1.5 flex items-center gap-1">
            <Lock className="h-3 w-3" /> Always included (required for re-import)
          </p>
          <div className="flex gap-4">
            {MANDATORY_COLS.map(col => (
              <span key={col.key} className="text-xs text-gray-600 font-medium">{col.label}</span>
            ))}
          </div>
        </div>

        {/* Select All / Deselect All */}
        <div className="flex items-center justify-between py-2 border-b border-gray-200">
          <button
            type="button"
            onClick={toggleAll}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
            data-testid="csv-toggle-all"
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
          <span className="text-xs text-gray-400">{selected.size} of {ALL_KEYS.length} optional columns</span>
        </div>

        {/* Optional column groups */}
        <div className="space-y-4 py-1">
          {COLUMN_GROUPS.map(group => (
            <div key={group.label}>
              <p className="text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">
                {group.label}
              </p>
              <div className="space-y-2 pl-1">
                {group.columns.map(col => (
                  <div key={col.key} className="flex items-center gap-3">
                    <Checkbox
                      id={`csv-col-${col.key}`}
                      checked={selected.has(col.key)}
                      onCheckedChange={() => toggleKey(col.key)}
                      data-testid={`csv-checkbox-${col.key}`}
                    />
                    <label
                      htmlFor={`csv-col-${col.key}`}
                      className="text-sm text-gray-700 cursor-pointer select-none"
                    >
                      {col.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2 border-t border-gray-200">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            data-testid="csv-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            onClick={handleDownload}
            disabled={noneSelected}
            data-testid="csv-download-confirm-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Download CSV
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
